/**
 * Listing type enum used across storefront and Cloud Functions.
 */
export const LISTING_TYPES = {
  NATIVE: 'native',
  FREE: 'free',
  LEGACY_EXTERNAL: 'legacyExternal',
  EXTERNAL_MARKETPLACE: 'externalMarketplace',
  LINK_ONLY: 'linkOnly',
};

/**
 * Payment provider enum used by normalized purchase records.
 */
export const PAYMENT_PROVIDERS = {
  STRIPE: 'stripe',
  SOLANA: 'solana',
  X402: 'x402',
  FREE: 'free',
};

/**
 * Solana assets supported by the initial release.
 */
export const SOLANA_SUPPORTED_ASSETS = ['SOL', 'USDC', 'BONK', 'SKR'];

const SOLANA_NETWORK = String(
  process.env.REACT_APP_SOLANA_CLUSTER || process.env.REACT_APP_SOLANA_NETWORK || 'mainnet-beta'
).toLowerCase();

const MAINNET_USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';
const DEVNET_USDC_MINT = '4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU';
const MAINNET_BONK_MINT = 'DezXAZ8z7PnrnRJjz3wXBoRgixCa6e7YaB1pPB263';

/**
 * Canonical token mint addresses used for product pricing rows.
 * Non-SOL addresses should come from this registry and not per-product edits.
 */
export const SOLANA_TOKEN_MINTS = {
  SOL: null,
  USDC:
    String(process.env.REACT_APP_SOLANA_USDC_MINT || '').trim() ||
    (SOLANA_NETWORK === 'devnet'
      ? DEVNET_USDC_MINT
      : SOLANA_NETWORK === 'mainnet-beta'
        ? MAINNET_USDC_MINT
        : ''),
  BONK:
    String(process.env.REACT_APP_SOLANA_BONK_MINT || '').trim() ||
    (SOLANA_NETWORK === 'mainnet-beta' ? MAINNET_BONK_MINT : ''),
  SKR: String(process.env.REACT_APP_SOLANA_SKR_MINT || '').trim(),
};

/**
 * Returns a default mint address for a Solana asset symbol.
 * @param {string} symbol Asset symbol.
 * @returns {string | null}
 */
export function getDefaultMintForAssetSymbol(symbol) {
  const normalizedSymbol = String(symbol || '').toUpperCase();
  if (normalizedSymbol === 'SOL') {
    return null;
  }
  return SOLANA_TOKEN_MINTS[normalizedSymbol] || '';
}

/**
 * Token decimals used for human amount <-> atomic amount conversion.
 */
export const SOLANA_TOKEN_DECIMALS = {
  SOL: 9,
  USDC: 6,
  BONK: 5,
  SKR: 6,
};

/**
 * Returns decimals for a token symbol.
 * @param {string} symbol Asset symbol.
 * @returns {number}
 */
export function getSolanaTokenDecimals(symbol) {
  const normalizedSymbol = String(symbol || '').toUpperCase();
  return SOLANA_TOKEN_DECIMALS[normalizedSymbol] ?? 0;
}

/**
 * Default product document used by admin forms.
 */
export const EMPTY_PRODUCT = {
  title: '',
  slug: '',
  description: '',
  productType: 'soundPack',
  listingType: LISTING_TYPES.NATIVE,
  isFree: false,
  isActive: true,
  isHomepageFeatured: false,
  coverImagePath: '',
  previewFilePath: '',
  downloadFilePath: '',
  includedFileTypes: [],
  licenseId: '',
  licenseUrl: '',
  attributionAuthor: '',
  originalAudioRightsConfirmed: false,
  tags: [],
  platformTags: [],
  stripeConfig: {
    enabled: true,
    priceId: '',
  },
  solanaConfig: {
    enabled: true,
    recipientWallet: '',
    acceptedAssets: [
      { symbol: 'SOL', mint: null, amount: 0, amountAtomic: 0 },
      { symbol: 'USDC', mint: getDefaultMintForAssetSymbol('USDC'), amount: 0, amountAtomic: 0 },
      { symbol: 'BONK', mint: getDefaultMintForAssetSymbol('BONK'), amount: 0, amountAtomic: 0 },
      { symbol: 'SKR', mint: getDefaultMintForAssetSymbol('SKR'), amount: 0, amountAtomic: 0 },
    ],
    defaultAsset: 'SOL',
  },
  externalConfig: {
    enabled: false,
    externalPlatform: '',
    externalUrl: '',
    buttonLabel: '',
    embedUrl: '',
    embedType: '',
    embedHtml: '',
  },
};
